/**
 * Contenu et mise en page du procès-verbal, partagés par l'export Word
 * (components/core/Download.js) et l'aperçu A4 (components/core/PvPreview.js).
 *
 * Le PV est décrit par une liste de nœuds neutres :
 * - paragraph : { type, alignment, spacing, indent, border, keepNext, runs, rubricId }
 * - table     : { type, borders, columnWidths, rows: [{ cells: [{ width, verticalAlign, children }] }] }
 * - image     : { type, src | logo, width, height, alignment }
 *
 * Un run vaut { text, bold, italics, underline, strike, color, highlight, size, font },
 * { break: true } (saut de ligne) ou { field: 'page' | 'pages', size } (numéro de page).
 *
 * Unités Word : twips (1/20 pt) pour les espacements et retraits, demi-points
 * pour les tailles de police, pourcentages pour les largeurs de cellules,
 * pixels pour les images.
 */
import { normalizeRubricHtml } from './normalizeRubricHtml';

export const PV_FONT_FAMILY = 'Helvetica';

export const PV_FONT_SIZE = {
  header: 18,
  body: 20,
  title: 24,
  proJustitia: 30,
};

// Interligne par défaut du document : 240 = simple, 276 = 1,15.
export const PV_LINE_SPACING = 276;

// Page A4. header, footer et gutter sont les valeurs par défaut de Word.
export const PV_PAGE = {
  width: 11906,
  height: 16838,
  margin: {
    top: 1440,
    right: 1440,
    bottom: 1080,
    left: 1440,
    header: 708,
    footer: 708,
    gutter: 0,
  },
};

export const PV_LOGO_SIZE = 40;

export const PV_LOGO_SVG = `<?xml version="1.0" encoding="utf-8"?>
<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 150 150" width="150" height="150">
  <circle cx="75" cy="75" r="74" fill="#FFFFFF" stroke="#244BA6" stroke-width="2"/>
  <path fill="#244BA6" d="M150,75c0-41.4-33.6-75-75-75S0,33.6,0,75c0,39.5,30.5,71.9,69.3,74.8c-3.3-4-7.4-9.5-11-14.6
    c-6.1-8.6-13.4-21.5-13.7-34.4c-0.2-9.6,2-14.8,14.6-34.7c4.9-7.8,8.2-21.1,7.7-23.9c-0.5-2.9,4.6,4.3,6.5,9.1
    c2.5,6.4,2.2,11.2-2.5,24.4c-2.2,6.3-5.9,16.4-2.2,20.9c-0.4-0.5,11.9-13.2,14.8-26.2c2.2-10,1.3-23.3-3-33.1
    c-3.8-8.6-8.3-15.7-7.4-27.1c0.4-4.8,1.7-9.4,1.8-9.7c2.8,7,4,9.5,5.1,11.5c5.1,9.6,15.6,23.7,20.5,33.4
    c5,9.6,8.8,25.5,3,41.7c-5.7,15.9-11.1,21.3-16.9,34.2c-4.3,9.6-7.9,21.4-9.7,28.8C117.5,149,150,115.9,150,75"/>
</svg>`;

// Fonction pour formater la date et heure de début (StartDate)
export const formatStartDate = (dateString) => {
  if (!dateString) return 'Non défini';

  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return dateString;

    const dateFormatee = date.toLocaleDateString('fr-FR', {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });

    const heures = date.getHours().toString().padStart(2, '0');
    const minutes = date.getMinutes().toString().padStart(2, '0');
    const heureFormatee = `${heures}:${minutes}`;

    // « heure » au singulier avant 02:00
    const pluriel = date.getHours() >= 2 ? 's' : '';

    return `${dateFormatee} à ${heureFormatee} heure${pluriel}`;
  } catch (error) {
    console.error('Erreur lors du formatage de la date de début:', error);
    return dateString;
  }
};

// Fonction pour formater la date de fin (EndDate)
export const formatEndDate = (dateString) => {
  if (!dateString) return 'Non défini';

  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return dateString;

    return date.toLocaleDateString('fr-FR', {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  } catch (error) {
    console.error('Erreur lors du formatage de la date de fin:', error);
    return dateString;
  }
};

/**
 * Paramètres de zone utilisés dans le PV, avec la valeur de repli de l'export.
 */
export const getPvSettings = (settings = {}) => {
  let policeType = 'N/A';
  if (settings.type === 'local') {
    policeType = 'Locale';
  } else if (settings.type === 'federal') {
    policeType = 'Fédérale';
  } else if (settings.type) {
    policeType = settings.type;
  }

  return {
    policeType,
    clientName: settings.clientName || 'N/A',
    zoneNumber: settings.zoneNumber || 'N/A',
    zoneNamePV: settings.zoneNamePV || 'N/A',
    shortJudicialDistrict: settings.shortJudicialDistrict || 'N/A',
    judicialDivision: settings.judicialDivision || 'N/A',
  };
};

export const getPvReference = (reportData, pvSettings) =>
  `${pvSettings.shortJudicialDistrict}.${reportData.indice || '__'}.${pvSettings.judicialDivision}.${reportData.number}/${reportData.year}`;

const getSignataires = (intervenants) =>
  (intervenants || []).filter((intervenant) => intervenant.sign === true);

const paragraph = (props = {}) => ({ type: 'paragraph', runs: [], ...props });

/**
 * En-tête répété sur chaque page : Logo | Infos gauche | PRO JUSTITIA | Infos droite,
 * puis un filet horizontal.
 */
export const buildPvHeader = (reportData, settings) => {
  const pv = getPvSettings(settings);
  const small = (text, props = {}) => ({ text, size: PV_FONT_SIZE.header, ...props });
  const cell = (width, children) => ({ width, verticalAlign: 'center', children });
  const reportType = reportData.reportType === 'subsequent' ? 'subséquent' : reportData.reportType;

  return [
    {
      type: 'table',
      borders: false,
      rows: [
        {
          cells: [
            cell(12, [{ type: 'image', logo: true, width: PV_LOGO_SIZE, height: PV_LOGO_SIZE }]),
            cell(28, [
              paragraph({ spacing: { after: 40 }, runs: [small(`Police ${pv.policeType}`)] }),
              paragraph({ spacing: { after: 40 }, runs: [small(`${pv.clientName} - ${pv.zoneNumber}`, { bold: true })] }),
              paragraph({ runs: [small(`Service ${reportData.unit}`)] }),
            ]),
            cell(30, [
              paragraph({
                alignment: 'center',
                runs: [{ text: 'PRO JUSTITIA', size: PV_FONT_SIZE.proJustitia, bold: true }],
              }),
            ]),
            cell(30, [
              paragraph({ alignment: 'right', spacing: { after: 40 }, runs: [small(`Procès-verbal ${reportType}`)] }),
              paragraph({
                alignment: 'right',
                spacing: { after: 40 },
                runs: [small(getPvReference(reportData, pv), { bold: true })],
              }),
              paragraph({
                alignment: 'right',
                runs: [
                  small('Suite '),
                  { field: 'page', size: PV_FONT_SIZE.header },
                  small(' sur '),
                  { field: 'pages', size: PV_FONT_SIZE.header },
                ],
              }),
            ]),
          ],
        },
      ],
    },
    paragraph({
      border: { bottom: { color: '000000', space: 1, size: 6 } },
      spacing: { after: 200, before: 0, line: 20 },
      runs: [{ text: '', size: 4, font: false }],
    }),
  ];
};

const spacer = (after) => paragraph({ spacing: { after } });
const lastname = (intervenant) => String(intervenant.lastname || '').toUpperCase();

/**
 * Introduction : date d'ouverture et signataires.
 */
export const buildPvIntroduction = (reportData, settings) => {
  const pv = getPvSettings(settings);
  const signataires = getSignataires(reportData.intervenants);
  const pluriel = signataires.length > 1 ? 's' : '';

  return [
    paragraph({
      alignment: 'center',
      spacing: { after: 0 },
      runs: [{ text: `Ce jourd'hui, le ${formatStartDate(reportData.StartDate)},` }],
    }),
    paragraph({
      alignment: 'center',
      spacing: { after: 0 },
      runs: [
        { text: `Nous soussigné${pluriel}, ` },
        ...signataires.flatMap((intervenant, index) => [
          { text: lastname(intervenant), bold: true },
          { text: ' ' },
          { text: intervenant.firstname, bold: true },
          { text: ', ' },
          ...(intervenant.premier ? [{ text: 'Premier ' }] : []),
          { text: intervenant.rank },
          ...(index < signataires.length - 1 ? [{ text: ', ' }] : []),
        ]),
      ],
    }),
    paragraph({
      alignment: 'center',
      spacing: { after: 0 },
      runs: [{ text: `attaché${pluriel} au service ${reportData.unit} de la ${pv.zoneNamePV},` }],
    }),
    paragraph({
      alignment: 'center',
      spacing: { after: 120 },
      runs: [{ text: 'portons à la connaissance de votre office :' }],
    }),
    spacer(60),
  ];
};

/**
 * Clôture : date de fin et place des signatures.
 */
export const buildPvClosing = (reportData) => {
  const signataires = getSignataires(reportData.intervenants);

  return [
    spacer(60),
    paragraph({
      alignment: 'right',
      spacing: { after: 60 },
      runs: [{ text: `Dont acte, clos le ${formatEndDate(reportData.EndDate)}` }],
    }),
    spacer(60),
    ...signataires.flatMap((intervenant) => [
      paragraph({
        alignment: 'right',
        spacing: { after: 60 },
        runs: [
          { text: intervenant.premier ? 'Premier ' : '' },
          { text: `${intervenant.rank || ''} ` },
          { text: lastname(intervenant), bold: true },
          { text: ' ' },
          { text: intervenant.firstname, bold: true },
        ],
      }),
      // Place laissée pour la signature
      spacer(800),
    ]),
  ];
};

/**
 * Rubrique : titre sous un filet, puis son contenu.
 */
export const buildPvRubric = (rubric) => [
  paragraph({
    rubricId: rubric.id,
    keepNext: true,
    border: { top: { color: '000000', space: 1, size: 6 } },
    spacing: { before: 240, after: 120 },
    runs: [{ text: rubric.title, bold: true, size: PV_FONT_SIZE.title }],
  }),
  ...htmlToPvBlocks(normalizeRubricHtml(rubric.content, { version: rubric.version })),
];

/**
 * Corps du PV : introduction, rubriques, clôture et signatures.
 */
export const buildPvBody = (reportData, settings) => {
  const rubrics = [...(reportData.rubrics || [])]
    .sort((a, b) => a.position - b.position)
    .flatMap((rubric) => [...buildPvRubric(rubric), spacer(200)]);

  return [
    ...buildPvIntroduction(reportData, settings),
    ...rubrics,
    ...buildPvClosing(reportData),
  ];
};

/* ---------- Conversion du HTML des rubriques ---------- */

const parseColorValue = (colorValue) => {
  if (!colorValue) return undefined;

  const trimmed = colorValue.trim();

  if (trimmed.startsWith('#')) {
    return trimmed.substring(1);
  }

  const rgbMatch = trimmed.match(/rgb\s*\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)\s*\)/i);
  if (rgbMatch) {
    const r = parseInt(rgbMatch[1]).toString(16).padStart(2, '0');
    const g = parseInt(rgbMatch[2]).toString(16).padStart(2, '0');
    const b = parseInt(rgbMatch[3]).toString(16).padStart(2, '0');
    return `${r}${g}${b}`;
  }

  const colorMap = {
    'black': '000000',
    'white': 'FFFFFF',
    'red': 'FF0000',
    'green': '00FF00',
    'blue': '0000FF',
    'yellow': 'FFFF00',
    'gray': '808080',
    'grey': '808080',
    'purple': '800080',
    'orange': 'FFA500',
  };

  return colorMap[trimmed.toLowerCase()] || undefined;
};

// Couleur du texte (color, mais pas background-color)
const extractColor = (style) => {
  if (!style) return undefined;
  const colorMatch = style.match(/(?<!background-)color\s*:\s*([^;]+)/i);
  return colorMatch && colorMatch[1] ? parseColorValue(colorMatch[1]) : undefined;
};

const extractBackgroundColor = (style) => {
  if (!style) return undefined;

  const bgColorMatch = style.match(/background-color\s*:\s*([^;]+)/i);
  if (bgColorMatch && bgColorMatch[1]) {
    const bgValue = bgColorMatch[1].trim().toLowerCase();
    if (bgValue === 'transparent' || bgValue === 'inherit' || bgValue === 'initial') {
      return undefined;
    }
    return parseColorValue(bgColorMatch[1]);
  }

  return undefined;
};

const extractAlignment = (style) => {
  if (!style) return undefined;

  if (style.match(/text-align\s*:\s*center/i)) return 'center';
  if (style.match(/text-align\s*:\s*right/i)) return 'right';
  if (style.match(/text-align\s*:\s*justify/i)) return 'justify';
  if (style.match(/text-align\s*:\s*left/i)) return 'left';

  return undefined;
};

const tableHasBorders = (tableElement) => {
  const tableStyle = (tableElement.getAttribute('style') || '').toLowerCase();
  const tableBorder = tableElement.getAttribute('border');

  if (tableBorder === '0') return false;

  if (tableStyle.includes('border:') || tableStyle.includes('border-style:')) {
    const hasNone =
      tableStyle.includes('border:none') ||
      tableStyle.includes('border: none') ||
      tableStyle.includes('border-style:none') ||
      tableStyle.includes('border-style: none');
    if (!hasNone) return true;
  }

  const cells = tableElement.querySelectorAll('td, th');
  for (const cell of cells) {
    const cellStyle = (cell.getAttribute('style') || '').toLowerCase();
    if (
      cellStyle.includes('border:') &&
      !cellStyle.includes('border:none') &&
      !cellStyle.includes('border: none') &&
      !cellStyle.includes('border-style:none') &&
      !cellStyle.includes('border-style: none')
    ) {
      return true;
    }
  }

  return Boolean(tableBorder && tableBorder !== '0');
};

// 1px ≈ 15 twips (96 dpi)
const pxToTwip = (px) => {
  const numeric = Number(px);
  if (Number.isNaN(numeric)) return null;
  return Math.round(numeric * 15);
};

// Texte avec sa mise en forme (gras, italique, souligné, barré, couleurs, sauts de ligne)
const inlineRuns = (element, inheritedStyles = {}) => {
  const runs = [];

  const visit = (node, styles) => {
    if (node.nodeType === Node.TEXT_NODE) {
      const text = node.textContent;
      if (!text) return;
      const normalizedText = text.replace(/\s+/g, ' ');
      // Un espace en tête de paragraphe est ignoré. Entre deux mots, y compris &nbsp;, il est conservé.
      if (normalizedText.trim() || runs.length > 0) {
        runs.push({
          text: normalizedText,
          bold: styles.bold || false,
          italics: styles.italic || false,
          underline: Boolean(styles.underline),
          strike: styles.strike || false,
          color: styles.color,
          highlight: styles.backgroundColor,
        });
      }
      return;
    }

    if (node.nodeType !== Node.ELEMENT_NODE) return;

    const tagName = node.tagName.toLowerCase();
    const nodeStyle = node.getAttribute('style') || '';
    const next = { ...styles };

    if (tagName === 'b' || tagName === 'strong' ||
        nodeStyle.includes('font-weight:bold') || nodeStyle.includes('font-weight: bold')) {
      next.bold = true;
    }
    if (tagName === 'i' || tagName === 'em' ||
        nodeStyle.includes('font-style:italic') || nodeStyle.includes('font-style: italic')) {
      next.italic = true;
    }
    if (tagName === 'u' ||
        nodeStyle.includes('text-decoration:underline') || nodeStyle.includes('text-decoration: underline')) {
      next.underline = true;
    }
    if (tagName === 's' || tagName === 'strike' ||
        nodeStyle.includes('text-decoration:line-through') || nodeStyle.includes('text-decoration: line-through')) {
      next.strike = true;
    }

    const color = extractColor(nodeStyle);
    if (color) next.color = color;

    const backgroundColor = extractBackgroundColor(nodeStyle);
    if (backgroundColor) next.backgroundColor = backgroundColor;

    if (tagName === 'br') {
      runs.push({ break: true });
      return;
    }
    for (const child of node.childNodes) {
      visit(child, next);
    }
  };

  for (const child of element.childNodes) {
    visit(child, inheritedStyles);
  }

  return runs;
};

const tableBlock = (tableElement) => {
  const hasBorders = tableHasBorders(tableElement);

  const colWidths = [];
  const colgroup = tableElement.querySelector('colgroup');
  if (colgroup) {
    colgroup.querySelectorAll('col').forEach((col) => {
      const widthMatch = (col.getAttribute('style') || '').match(/width\s*:\s*(\d+)px/i);
      colWidths.push(widthMatch ? parseInt(widthMatch[1]) : null);
    });
  }

  const rows = [];
  for (const htmlRow of tableElement.querySelectorAll('tr')) {
    const cells = [];
    let cellIndex = 0;

    for (const htmlCell of htmlRow.querySelectorAll('td, th')) {
      const isHeader = htmlCell.tagName === 'TH';
      const alignment = extractAlignment(htmlCell.getAttribute('style') || '');
      // Largeur en pourcentage, sur une base de 500px de largeur de page
      const width = colWidths[cellIndex] ? Math.round((colWidths[cellIndex] / 500) * 100) : null;

      // Chaque div feuille (pers-concernees, parties…) est une ligne de la cellule
      const children = [];
      const divs = htmlCell.getElementsByTagName('div');
      for (let i = 0; i < divs.length; i++) {
        const div = divs[i];
        const hasContent = div.textContent.trim().length > 0;
        const isContainerDiv = div.getElementsByTagName('div').length > 0;
        if (hasContent && !isContainerDiv) {
          const runs = inlineRuns(div, { bold: isHeader });
          if (runs.length > 0) {
            children.push(paragraph({ alignment, spacing: { after: 0, line: 240 }, runs }));
          }
        }
      }

      if (children.length === 0) {
        const runs = inlineRuns(htmlCell, { bold: isHeader });
        children.push(paragraph({
          alignment,
          runs: runs.length > 0 ? runs : [{ text: htmlCell.textContent.trim() || ' ', bold: isHeader }],
        }));
      }

      cells.push({ width: width || undefined, verticalAlign: 'top', children });
      cellIndex++;
    }

    if (cells.length > 0) {
      rows.push({ cells });
    }
  }

  if (rows.length === 0) return null;

  const table = { type: 'table', borders: hasBorders, rows };
  if (colWidths.length && colWidths.every((w) => typeof w === 'number')) {
    const twipWidths = colWidths.map(pxToTwip).filter((w) => w !== null);
    if (twipWidths.length === colWidths.length) {
      table.columnWidths = twipWidths;
    }
  }
  return table;
};

// Éléments de type bloc : chacun devient un paragraphe ou un élément séparé
const BLOCK_TAGS = new Set([
  'P', 'DIV', 'UL', 'OL', 'TABLE', 'BLOCKQUOTE',
  'H1', 'H2', 'H3', 'H4', 'H5', 'H6', 'BR', 'IMG', 'HR',
]);

/**
 * Convertit le HTML (normalisé) d'une rubrique en nœuds du PV.
 */
export const htmlToPvBlocks = (html) => {
  if (!html) return [];

  const container = document.createElement('div');
  container.innerHTML = html;

  const blocks = [];

  const pushImage = (img, alignment) => {
    const src = img.getAttribute('src');
    if (!src) return;
    blocks.push({
      type: 'image',
      src,
      width: Math.min(parseInt(img.getAttribute('width')) || 200, 500),
      height: Math.min(parseInt(img.getAttribute('height')) || 150, 400),
      alignment: alignment || 'center',
    });
  };

  const processElement = (element) => {
    const tagName = element.tagName?.toUpperCase();
    const style = element.getAttribute?.('style') || '';
    const alignment = extractAlignment(style);

    if (tagName === 'TABLE') {
      const table = tableBlock(element);
      if (table) {
        blocks.push(table);
        blocks.push(paragraph({ spacing: { after: 120 } }));
      }
    } else if (tagName === 'IMG') {
      pushImage(element, alignment);
    } else if (tagName === 'UL' || tagName === 'OL') {
      element.querySelectorAll(':scope > li').forEach((item, idx) => {
        blocks.push(paragraph({
          alignment,
          runs: [{ text: tagName === 'UL' ? '• ' : `${idx + 1}. ` }, ...inlineRuns(item)],
          indent: { left: 360, hanging: 240 },
        }));
      });
    } else if (tagName === 'BLOCKQUOTE') {
      const runs = inlineRuns(element);
      blocks.push(paragraph({
        alignment,
        runs: runs.length > 0 ? runs : [{ text: element.textContent.trim(), italics: true }],
        indent: { left: 720 },
        border: { left: { color: '999999', size: 12 } },
      }));
    } else if (tagName === 'P' || tagName === 'DIV' || tagName?.match(/^H[1-6]$/)) {
      // Un bloc qui contient un tableau est traité enfant par enfant
      if (element.querySelector('table')) {
        for (const child of element.childNodes) {
          if (child.nodeType === Node.TEXT_NODE && !child.textContent.trim()) continue;
          if (child.nodeType === Node.ELEMENT_NODE) {
            processElement(child);
          } else if (child.nodeType === Node.TEXT_NODE) {
            blocks.push(paragraph({ alignment, runs: [{ text: child.textContent }] }));
          }
        }
        return;
      }

      // Les images du bloc sont placées avant son texte
      const images = element.getElementsByTagName('img');
      for (let i = 0; i < images.length; i++) {
        pushImage(images[i], alignment);
      }

      const textContent = element.textContent.trim();
      if (textContent) {
        const runs = inlineRuns(element);
        let headingSize = PV_FONT_SIZE.body;
        if (tagName?.match(/^H[1-6]$/)) {
          const level = parseInt(tagName.substring(1));
          headingSize = Math.max(PV_FONT_SIZE.body, 36 - (level * 4));
        }
        blocks.push(paragraph({
          alignment,
          spacing: { after: 120 },
          runs: runs.length > 0 ? runs : [{ text: textContent, size: headingSize }],
        }));
      }
    } else if (tagName === 'BR') {
      blocks.push(paragraph());
    } else if (element.children && element.children.length > 0) {
      for (const child of element.children) {
        processElement(child);
      }
    } else if (element.textContent?.trim()) {
      const runs = inlineRuns(element);
      blocks.push(paragraph({
        alignment,
        runs: runs.length > 0 ? runs : [{ text: element.textContent.trim() }],
      }));
    }
  };

  const childNodes = Array.from(container.childNodes);
  const hasBlockChild = childNodes.some((node) =>
    node.nodeType === Node.ELEMENT_NODE && BLOCK_TAGS.has(node.tagName)
  );
  const hasNonEmptyText = childNodes.some((node) =>
    node.nodeType === Node.TEXT_NODE && node.textContent.trim()
  );
  const hasInlineElements = childNodes.some((node) =>
    node.nodeType === Node.ELEMENT_NODE && !BLOCK_TAGS.has(node.tagName)
  );

  if (hasBlockChild && (hasNonEmptyText || hasInlineElements)) {
    // Contenu mixte (« Nous joignons les annexes suivantes :<ul>…</ul> ») :
    // le texte et les éléments inline consécutifs forment un paragraphe
    let inlineBuffer = [];

    const flushInlineBuffer = () => {
      if (inlineBuffer.length === 0) return;
      const tempSpan = document.createElement('span');
      inlineBuffer.forEach((n) => tempSpan.appendChild(n.cloneNode(true)));
      const runs = inlineRuns(tempSpan);
      if (runs.length > 0) {
        blocks.push(paragraph({ spacing: { after: 120 }, runs }));
      }
      inlineBuffer = [];
    };

    for (const child of childNodes) {
      if (child.nodeType === Node.ELEMENT_NODE && BLOCK_TAGS.has(child.tagName)) {
        flushInlineBuffer();
        processElement(child);
      } else if (child.nodeType === Node.TEXT_NODE && !child.textContent.trim()) {
        continue;
      } else {
        inlineBuffer.push(child);
      }
    }

    flushInlineBuffer();
  } else {
    const hasOnlyElements = childNodes.every((node) =>
      node.nodeType === Node.ELEMENT_NODE ||
      (node.nodeType === Node.TEXT_NODE && !node.textContent.trim())
    );

    if (hasOnlyElements && container.children.length > 0) {
      for (const child of container.children) {
        processElement(child);
      }
    } else if (container.childNodes.length > 0) {
      // Texte mêlé d'éléments inline : un seul paragraphe
      const runs = inlineRuns(container);
      if (runs.length > 0) {
        blocks.push(paragraph({ spacing: { after: 120 }, runs }));
      }
    } else if (container.textContent.trim()) {
      blocks.push(paragraph({ runs: [{ text: container.textContent.trim() }] }));
    }
  }

  return blocks;
};
